import { useEnum } from "@/api/services/global/enum";
import { useGetNationalityList } from "@/api/services/global/master";
import { Checkbox } from "@/components/elements/Checkbox";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { RadioGroup } from "@/components/elements/RadioGroup";
import { Select } from "@/components/elements/Select";
import { CityInput } from "@/components/elements/CityInput";
import { AvatarUploadForm } from "@/features/talent/components/talent-form/AvatarUploadForm";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { black, divider } from "@/theme/colors";
import { FCC } from "@/types/common";
import { ERecruitmentStatus, TALENT_LOCATION, TALENT_LOCATION_OPTIONS } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { Box, Grid, Stack, Typography, Collapse } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { blue } from "@/theme/colors";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useCallback, useEffect, useState } from "react";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";

const CIRCLED_NUMBERS = ["①", "②", "③", "④"];
const MAX_CONTACT_METHODS = 4;

type TalentFormBasicInfoProps = {};

export const TalentFormBasicInfo: FCC<TalentFormBasicInfoProps> = () => {
  const methods = useFormContext<TTalentForm>();
  const { control, setValue, trigger } = methods;
  const { data: genderOptions } = useEnum("EGender");
  const { data: contactTypeOptions } = useEnum("ETalentContactType");
  const { nationalityOptions } = useGetNationalityList();
  const recruitmentStatus = useWatch({ name: "recruitmentStatus" });
  const isDraft = recruitmentStatus === ERecruitmentStatus.DRAFT;
  const nickName = useWatch({ name: "nickName" });

  const location = useWatch({ name: "location" });
  const isInJapan = location === TALENT_LOCATION.LOCAL;
  const isRecommendedTalent = useWatch({ name: "isRecommendedTalent" });

  const {
    fields: contactMethodFields,
    append: appendContactMethod,
    remove: removeContactMethod,
  } = useFieldArray({
    control,
    name: "contactMethods",
  });

  const watchedContactMethods = useWatch({ control, name: "contactMethods" });

  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  const handleRemoveContactMethod = useCallback(
    (index: number) => {
      const method = watchedContactMethods?.[index];
      if (method?.contactType != null || (method?.contactInfo != null && method.contactInfo !== "")) {
        setDeleteConfirmIndex(index);
      } else {
        removeContactMethod(index);
      }
    },
    [watchedContactMethods, removeContactMethod],
  );

  // Filter out already-selected contact types from other rows' options
  const getFilteredContactTypeOptions = useCallback(
    (currentIndex: number) => {
      const selectedTypes = (watchedContactMethods ?? [])
        .filter((_, index) => index !== currentIndex)
        .map((method) => method?.contactType)
        .filter((type): type is number => type != null);

      return (contactTypeOptions ?? []).filter((option) => !selectedTypes.includes(Number(option.value)));
    },
    [watchedContactMethods, contactTypeOptions],
  );

  const locationInfo = useLocationInfo({
    methods,
    names: { zipCode: "zipCode", province: "prefecture", municipality: "city", address: "address" },
  });

  useEffect(() => {
    if (location === TALENT_LOCATION.OVERSEA) {
      setValue("zipCode", "");
      setValue("prefecture", null);
      setValue("city", null);
      setValue("address", null);
    }
  }, [location, setValue]);

  // Re-validate fullName when nickName or recruitmentStatus changes,
  // but only after form has been interacted with to avoid premature errors
  const { isSubmitted, touchedFields } = methods.formState;
  useEffect(() => {
    if (isSubmitted || touchedFields.fullName || touchedFields.nickName) {
      trigger("fullName");
    }
  }, [nickName, recruitmentStatus, trigger, isSubmitted, touchedFields.fullName, touchedFields.nickName]);

  const { expanded, toggle } = useCollapsible();

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      {/* Start Basic Information */}
      <CollapsibleSectionHeader title="基本情報" expanded={expanded} onToggle={toggle}>
        <Box ml="auto" onClick={(e) => e.stopPropagation()}>
          <FormField control={control} name="isRecommendedTalent">
            <Checkbox
              label={
                <Stack direction="row" alignItems="center" spacing={1}>
                  おすすめ人材に設定
                </Stack>
              }
              checked={!!isRecommendedTalent}
            />
          </FormField>
        </Box>
      </CollapsibleSectionHeader>

      <Collapse in={expanded}>
      <Stack mt={3}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="fullName" required={!isDraft || !nickName?.trim()}>
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="fullNameKana">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="nickName">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="birthday">
              <InputDatePicker minDate={dayjs("1870/01/01")} maxDate={dayjs().subtract(18, "year")} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="talentNumber">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="nationalityId" required={!isDraft}>
              <Select placeholder="選択してください" options={nationalityOptions} />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="gender" required={!isDraft}>
              <RadioGroup direction="row" options={genderOptions} />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="phoneNumber">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="email">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      {/* Contact Methods */}

      {contactMethodFields.map((field, index) => (
        <Stack key={field.id} mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField
                control={control}
                name={`contactMethods.${index}.contactType`}
                label={`その他の連絡手段・SNS${CIRCLED_NUMBERS[index] ?? index + 1}`}
              >
                <Select placeholder="選択してください" options={getFilteredContactTypeOptions(index)} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField
                control={control}
                name={`contactMethods.${index}.contactInfo`}
                label={`その他の連絡先ID等${CIRCLED_NUMBERS[index] ?? index + 1}`}
              >
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
          </Grid>
          <Stack direction="row" alignItems="center" spacing={2} mt={1}>
            {index === contactMethodFields.length - 1 && contactMethodFields.length < MAX_CONTACT_METHODS && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ cursor: "pointer" }}
                onClick={() => appendContactMethod({ contactType: null, contactInfo: null })}
              >
                <AddCircleOutlineIcon sx={{ color: blue[80], fontSize: 18 }} />
                <Typography variant="cap12">その他の連絡手段・SNS、連絡先ID等を追加</Typography>
              </Stack>
            )}
            {contactMethodFields.length > 1 && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={() => handleRemoveContactMethod(index)}>
                <ClearIcon sx={{ color: blue[80], fontSize: 18, display: "flex" }} />
                <Typography variant="cap12">削除</Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      ))}

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="location" required={!isDraft}>
              <Select placeholder="選択してください" options={TALENT_LOCATION_OPTIONS} />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <FormField control={control} name="religion">
              <Input placeholder="入力してください" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      {/* End Basic Information */}

      {/* Start Location */}
      <Stack mt={3} direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="sub16Bold" color={black[30]}>
            現住所（国内）
          </Typography>
        </Box>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField control={control} name="zipCode">
              <Input placeholder="入力してください" disabled={!isInJapan} />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <FormField control={control} name="prefecture">
              <Input placeholder="入力してください" readonly />
            </FormField>
          </Grid>
          <Grid item xs={6}>
            <CityInput name="city" control={control} zipCodeFieldName="zipCode" locationInfo={locationInfo} disabled={!isInJapan} />
          </Grid>
        </Grid>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField control={control} name="address">
              <Input placeholder="入力してください" disabled={!isInJapan} />
            </FormField>
          </Grid>
        </Grid>
      </Stack>
      {/* End Location */}

      {/* Start Residence */}
      <Stack mt={3} direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="sub16Bold" color={black[30]}>
            本国における居住地
          </Typography>
        </Box>
      </Stack>

      <Stack mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField control={control} name="country">
              <Input placeholder="入力してください" autoComplete="off" />
            </FormField>
          </Grid>
        </Grid>
      </Stack>
      {/* End Residence */}

      {/* Upload profile */}
      <Stack mt={3} direction="row" alignItems="center" spacing={2}>
        <Box flex={1}>
          <Typography variant="sub16Bold" color={black[30]}>
            プロフィール写真のアップロード
          </Typography>
        </Box>
      </Stack>

      <Stack mt={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormField control={control} name="avatarFile">
              <AvatarUploadForm />
            </FormField>
          </Grid>
        </Grid>
      </Stack>

      {/* End Residence */}

      </Collapse>

      <ConfirmDeleteModal
        open={deleteConfirmIndex !== null}
        onClose={() => setDeleteConfirmIndex(null)}
        onOk={() => {
          if (deleteConfirmIndex !== null) {
            removeContactMethod(deleteConfirmIndex);
            setDeleteConfirmIndex(null);
          }
        }}
        message="入力されたデータが削除されます。よろしいですか？"
      />
    </InnerBox>
  );
};
